import { z } from 'zod';

/**
 * A referral code as it appears in `?r=CODE`, in a WhatsApp message and
 * on a partner's flyer.
 *
 * Uppercase alphanumerics only, 4–16 chars, so it survives being read
 * aloud, typed on a phone, and pasted into a URL without encoding.
 * Normalised on parse: trimmed and upper-cased, so `abs123` and
 * `ABS123` are the same code. Hyphens are accepted on input for
 * readability (`ABS-123`) and stripped.
 */
export const referralCodeSchema = z
  .string()
  .trim()
  .transform(value => value.replace(/-/g, '').toUpperCase())
  .pipe(z.string().regex(/^[A-Z0-9]{4,16}$/, 'referral code must be 4–16 letters or digits'));
export type ReferralCode = z.infer<typeof referralCodeSchema>;

/**
 * Alphabet used when the backend generates a code: no `0/O` and no
 * `1/I/L`, so a code read over the phone is unambiguous.
 */
export const REFERRAL_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

/**
 * Lengths the admin can pick for a generated code. `6` is the
 * recommended default (31⁶ ≈ 887M combinations — collisions are a
 * retry, not a risk). `4` gives a code people can remember from a
 * flyer or a voice call (31⁴ ≈ 924K), fine for a handful of partners
 * but the pool is small enough that it is a choice, not the default.
 */
export const REFERRAL_CODE_LENGTHS = [4, 5, 6, 8] as const;
export const REFERRAL_CODE_DEFAULT_LENGTH = 6;
export const referralCodeLengthSchema = z
  .union([z.literal(4), z.literal(5), z.literal(6), z.literal(8)])
  .default(REFERRAL_CODE_DEFAULT_LENGTH);
export type ReferralCodeLength = (typeof REFERRAL_CODE_LENGTHS)[number];

/**
 * The query parameter every Hero surface reads a referral code from.
 * One name across the landing site, the dashboard, the embed script and
 * `wa.me` prefills, so a code is never lost between surfaces.
 */
export const REFERRAL_QUERY_PARAM = 'r';

/**
 * First-party cookie names set by the embed script, the landing page,
 * and the dashboard (the mobile apps use the same keys in AsyncStorage).
 *
 * - `hero_r`      — the **original** code (first touch wins, never
 *                   overwritten). Owns attribution and rewards.
 * - `hero_r_last` — the **latest** code seen. Overwritten on every new
 *                   `?r=`, so a later campaign / product link can be
 *                   analysed in PostHog (`referral_code_current`) and
 *                   reported on the hit (`context.originalCode`) without
 *                   stealing the original's credit.
 * - `hero_vid`    — a random visitor id used for hit dedupe and lead
 *                   stitching.
 */
export const REFERRAL_COOKIE_CODE = 'hero_r';
export const REFERRAL_COOKIE_CODE_LAST = 'hero_r_last';
export const REFERRAL_COOKIE_VISITOR = 'hero_vid';

/**
 * Pull a referral code out of free text — a WhatsApp first message such
 * as `r=ABS123`, `code: ABS123`, `ABS-123`, or a pasted URL with
 * `?r=ABS123`. Returns the normalised code or `undefined`.
 *
 * Deliberately conservative: it only recognises the `r=` / `ref=` /
 * `code` markers or a URL query, never a bare token, because a bare
 * 6-char alphanumeric is also a plate number, an order ref and half the
 * words in a Saudi address.
 */
export const parseReferralCodeFromText = (text: string | undefined | null): string | undefined => {
  if (!text) return undefined;
  const match =
    text.match(/(?:[?&]|\b)(?:r|ref|referral|code)\s*[:=]\s*([A-Za-z0-9-]{4,17})/i) ??
    text.match(/\bكود\s*[:=]?\s*([A-Za-z0-9-]{4,17})/);
  if (!match) return undefined;
  const parsed = referralCodeSchema.safeParse(match[1]);
  return parsed.success ? parsed.data : undefined;
};
